'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select as SelectPrimitive } from 'radix-ui';
import { tv } from 'tailwind-variants';
import { faCheck, faChevronDown, faChevronUp } from '@icons/classic/regular';
import { Body } from '../typography/Body';
import { label } from '../typography/Label';
import type { VariantProps } from 'tailwind-variants';
const select = tv({
  slots: {
    root: 'w-full md:w-auto',
    group: '',
    value: '',
    trigger: `
      border-input
      data-placeholder:text-content-secondary
      [&_svg:not([class*=_text-_])]:text-muted-foreground
      aria-invalid:ring-destructive/20
      focus-visible:ring-primary
      dark:aria-invalid:ring-destructive/40
      aria-invalid:border-destructive
      flex
      w-fit
      shrink
      min-w-full
      items-center
      justify-between
      gap-2
      rounded-md
      border
      bg-transparent
      px-3
      text-sm
      whitespace-nowrap
      shadow-xs
      transition-[color,box-shadow]
      outline-none
      ring-inset
      focus-visible:ring-1
      disabled:cursor-not-allowed
      disabled:opacity-50
      [&_svg]:pointer-events-none
      [&_svg]:shrink-0
      [&_svg:not([class*=_size-_])]:size-4
      *:[data-slot=select-value]:line-clamp-1
      *:[data-slot=select-value]:flex
      *:[data-slot=select-value]:items-center
      *:[data-slot=select-value]:gap-2
      cursor-pointer
      data-[state=open]:ring-1 
      data-[state=open]:ring-primary
    `,
    content: `
      bg-popover
      text-popover-foreground
      data-[state=open]:animate-in
      data-[state=closed]:animate-out
      data-[state=closed]:fade-out-0
      data-[state=open]:fade-in-0
      data-[state=closed]:zoom-out-95
      data-[state=open]:zoom-in-95
      data-[side=bottom]:slide-in-from-top-2
      data-[side=left]:slide-in-from-right-2
      data-[side=right]:slide-in-from-left-2
      data-[side=top]:slide-in-from-bottom-2
      relative
      z-50
      max-h-(--radix-select-content-available-height)
      origin-(--radix-select-content-transform-origin)
      overflow-x-hidden
      overflow-y-auto
      rounded-md
      border
      shadow-md
      `,
    contentPopper: `
      data-[side=bottom]:translate-y-1
      data-[side=left]:-translate-x-1
      data-[side=right]:translate-x-1
      data-[side=top]:-translate-y-1
    `,
    viewport: 'p-1',
    viewportPopper: `
      h-(--radix-select-trigger-height)
      w-(--radix-select-trigger-width)
      scroll-my-1
    `,
    item: `
      focus:bg-background-secondary
      dark:focus:bg-background-primary
      focus:text-content-primary
      [&_svg:not([class*=_text-_])]:text-muted-foreground
      relative
      flex
      w-full
      cursor-default
      items-center
      gap-2
      rounded-sm
      py-1.5
      pr-8
      pl-2
      text-sm
      outline-hidden
      select-none
      whitespace-nowrap
      data-disabled:pointer-events-none
      data-disabled:opacity-50
      [&_svg]:pointer-events-none
      [&_svg]:shrink-0
      [&_svg:not([class*=_size-_])]:size-4
      *:[span]:last:flex
      *:[span]:last:items-center
      *:[span]:last:gap-2
    `,
    itemIndicator: `
      absolute
      right-2
      flex
      size-3.5
      items-center
      justify-center
    `,
    separator: `
      bg-border-secondary
      pointer-events-none
      -mx-1
      my-1
      h-px
    `,
    scrollButton: `
      flex
      cursor-default
      items-center
      justify-center
      py-1
    `
  },
  variants: {
    size: {
      sm: {
        trigger: 'h-8'
      },
      default: {
        trigger: 'h-10'
      }
    },
    fullWidth: {
      true: {
        trigger: 'w-full',
        root: 'w-full'
      }
    }
  },
  defaultVariants: {
    size: 'default'
  }
});
const {
  root,
  group,
  value,
  trigger,
  content,
  contentPopper,
  viewport,
  viewportPopper,
  item,
  itemIndicator,
  separator,
  scrollButton
} = select();
type SelectProps = React.ComponentProps<typeof SelectPrimitive.Root> & VariantProps<typeof select> & {
  className?: string;
};
function Select(t0) {
  const $ = _c(12);
  let className;
  let fullWidth;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      fullWidth,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = fullWidth;
    $[3] = props;
  } else {
    className = $[1];
    fullWidth = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className || $[5] !== fullWidth) {
    t1 = root({
      className,
      fullWidth
    });
    $[4] = className;
    $[5] = fullWidth;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const rootClass = t1;
  let t2;
  if ($[7] !== props) {
    t2 = <SelectPrimitive.Root {...props} />;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== rootClass || $[10] !== t2) {
    t3 = <div className={rootClass} data-slot="select-wrapper">{t2}</div>;
    $[9] = rootClass;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
type SelectGroupProps = React.ComponentProps<typeof SelectPrimitive.Group>;
function SelectGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = group({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Group className={t1} data-slot="select-group" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type SelectValueProps = React.ComponentProps<typeof SelectPrimitive.Value>;
function SelectValue(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = value({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Value className={t1} data-slot="select-value" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type SelectTriggerProps = React.ComponentProps<typeof SelectPrimitive.Trigger> & VariantProps<typeof select>;
function SelectTrigger(t0) {
  const $ = _c(15);
  let children;
  let className;
  let fullWidth;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      children,
      fullWidth,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = fullWidth;
    $[4] = props;
    $[5] = size;
  } else {
    children = $[1];
    className = $[2];
    fullWidth = $[3];
    props = $[4];
    size = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== fullWidth || $[8] !== size) {
    t1 = trigger({
      size,
      className,
      fullWidth
    });
    $[6] = className;
    $[7] = fullWidth;
    $[8] = size;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectPrimitive.Icon asChild={true}><FontAwesomeIcon className="size-4 opacity-50" icon={faChevronDown} /></SelectPrimitive.Icon>;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== children || $[12] !== props || $[13] !== t1) {
    t3 = <SelectPrimitive.Trigger className={t1} data-slot="select-trigger" {...props}>{children}{t2}</SelectPrimitive.Trigger>;
    $[11] = children;
    $[12] = props;
    $[13] = t1;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
type SelectContentProps = React.ComponentProps<typeof SelectPrimitive.Content>;
function SelectContent(t0) {
  const $ = _c(20);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const position = t1 === undefined ? "popper" : t1;
  let t2;
  if ($[5] !== className || $[6] !== position) {
    t2 = content({
      className: [position === "popper" && contentPopper(), className]
    });
    $[5] = className;
    $[6] = position;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectScrollUpButton />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== position) {
    t4 = viewport({
      className: position === "popper" ? viewportPopper() : ""
    });
    $[9] = position;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== children || $[12] !== t4) {
    t5 = <SelectPrimitive.Viewport className={t4}>{children}</SelectPrimitive.Viewport>;
    $[11] = children;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SelectScrollDownButton />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== position || $[16] !== props || $[17] !== t2 || $[18] !== t5) {
    t7 = <SelectPrimitive.Portal><SelectPrimitive.Content className={t2} data-slot="select-content" position={position} {...props}>{t3}{t5}{t6}</SelectPrimitive.Content></SelectPrimitive.Portal>;
    $[15] = position;
    $[16] = props;
    $[17] = t2;
    $[18] = t5;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
type SelectLabelProps = React.ComponentProps<typeof SelectPrimitive.Label>;
const selectLabel = tv({
  extend: label,
  base: 'p-1 uppercase',
  defaultVariants: {
    size: 'sm',
    weight: 'medium'
  }
});
function SelectLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = selectLabel({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Label className={t1} data-slot="select-label" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type SelectItemProps = React.ComponentProps<typeof SelectPrimitive.Item> & {
  subtext?: string;
};
function SelectItem(t0) {
  const $ = _c(16);
  let children;
  let className;
  let props;
  let subtext;
  if ($[0] !== t0) {
    ({
      className,
      children,
      subtext,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = subtext;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    subtext = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = item({
      className
    });
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = itemIndicator();
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className={t2}><SelectPrimitive.ItemIndicator><FontAwesomeIcon className="size-4" icon={faCheck} /></SelectPrimitive.ItemIndicator></span>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== subtext) {
    t4 = subtext ? <div className="flex flex-col"><SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText><Body className="whitespace-normal text-content-tertiary" size="sm">{subtext}</Body></div> : <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>;
    $[9] = children;
    $[10] = subtext;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props || $[13] !== t1 || $[14] !== t4) {
    t5 = <SelectPrimitive.Item className={t1} data-slot="select-item" {...props}>{t3}{t4}</SelectPrimitive.Item>;
    $[12] = props;
    $[13] = t1;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
type SelectSeparatorProps = React.ComponentProps<typeof SelectPrimitive.Separator>;
function SelectSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = separator({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Separator className={t1} data-slot="select-separator" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type SelectScrollUpButtonProps = React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>;
function SelectScrollUpButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = scrollButton({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FontAwesomeIcon className="size-4" icon={faChevronUp} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollUpButton className={t1} data-slot="select-scroll-up-button" {...props}>{t2}</SelectPrimitive.ScrollUpButton>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
type SelectScrollDownButtonProps = React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>;
function SelectScrollDownButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = scrollButton({
      className
    });
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FontAwesomeIcon className="size-4" icon={faChevronDown} />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollDownButton className={t1} data-slot="select-scroll-down-button" {...props}>{t2}</SelectPrimitive.ScrollDownButton>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export { select, Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem, SelectSeparator, SelectScrollUpButton, SelectScrollDownButton, type SelectProps, type SelectGroupProps, type SelectValueProps, type SelectTriggerProps, type SelectContentProps, type SelectLabelProps, type SelectItemProps, type SelectSeparatorProps, type SelectScrollUpButtonProps, type SelectScrollDownButtonProps };